// 03-11 (Pau: «siempre me abre una pestaña nueva»). Los documentos del chat se abren aquí, dentro de la oficina, como las
// imágenes (VisorImagen): un informe con el diseño de Venuz, un PDF con el lector del navegador dentro de la ventana, un
// texto tal cual. Se cierra con la X, con Escape o pulsando fuera. «Descargar» guarda el archivo (nunca abre pestaña).
import { Download, FileText, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { t } from '../../../i18n';
import { getActiveBaseUrl } from '../../../stores/servers.store';
import { esInforme } from '../informe/bloques';
import { Informe } from '../informe/Informe';

export interface DocumentoAbierto {
  agentId: string;
  id: string;
  nombre: string;
  tipo: string;
}

interface EstadoVisorDocumento {
  abierto: DocumentoAbierto | null;
  abrir: (d: DocumentoAbierto) => void;
  cerrar: () => void;
}

export const useVisorDocumento = create<EstadoVisorDocumento>((set) => ({
  abierto: null,
  abrir: (abierto) => set({ abierto }),
  cerrar: () => set({ abierto: null }),
}));

export const urlArchivo = (agentId: string, id: string) => `${getActiveBaseUrl()}/api/agents/${encodeURIComponent(agentId)}/attachments/${encodeURIComponent(id)}/raw`;

/** Las imágenes de un informe llegan como «adjunto:<id>» (backend/src/oficina/informe.ts); otra ruta no se puede enseñar. */
export function imagenDeInforme(agentId: string, src: string): string | null {
  const m = /^adjunto:([\w-]+)$/.exec(src);
  return m ? urlArchivo(agentId, m[1]!) : null;
}

type Contenido = { tipo: 'cargando' } | { tipo: 'error' } | { tipo: 'informe' | 'texto'; texto: string } | { tipo: 'pdf'; url: string };

function useContenido(d: DocumentoAbierto | null): Contenido {
  const [c, setC] = useState<Contenido>({ tipo: 'cargando' });
  useEffect(() => {
    if (!d) return;
    let vivo = true;
    let objeto: string | null = null;
    setC({ tipo: 'cargando' });
    fetch(urlArchivo(d.agentId, d.id), { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status));
        if (d.tipo === 'application/pdf') {
          // El PDF como blob propio: el lector del navegador lo pinta dentro de la ventana sin volver a pedir permiso.
          objeto = URL.createObjectURL(new Blob([await r.arrayBuffer()], { type: 'application/pdf' }));
          if (vivo) setC({ tipo: 'pdf', url: objeto });
          return;
        }
        const texto = await r.text();
        if (vivo) setC({ tipo: esInforme({ tipo: d.tipo, nombre: d.nombre }) ? 'informe' : 'texto', texto });
      })
      .catch(() => vivo && setC({ tipo: 'error' }));
    return () => {
      vivo = false;
      if (objeto) URL.revokeObjectURL(objeto);
    };
  }, [d]);
  return c;
}

export function VisorDocumento() {
  const { abierto, cerrar } = useVisorDocumento();
  const contenido = useContenido(abierto);
  const boton = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!abierto) return;
    const alTeclado = (e: KeyboardEvent) => {
      // La imagen ampliada de un informe va encima: Escape la cierra a ella primero (VisorImagen escucha también).
      if (e.key === 'Escape' && !document.querySelector('[data-visor-imagen]')) cerrar();
    };
    window.addEventListener('keydown', alTeclado);
    boton.current?.focus();
    return () => window.removeEventListener('keydown', alTeclado);
  }, [abierto, cerrar]);
  if (!abierto) return null;
  const informe = contenido.tipo === 'informe';
  return (
    <div role="dialog" aria-modal="true" aria-label={abierto.nombre} data-testid="visor-documento" className="fixed inset-0 z-[55] flex items-stretch justify-center bg-black/75 p-4 animate-in fade-in-0 sm:p-8" onClick={cerrar}>
      <div className="flex min-h-0 w-full max-w-[1100px] flex-col overflow-hidden rounded-o-l border border-oficina-linea bg-oficina-fondo shadow-o-hoja" onClick={(e) => e.stopPropagation()}>
        <header className="flex shrink-0 items-center gap-3 border-b border-oficina-linea bg-oficina-panel px-5 py-3">
          <FileText className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-o-texto font-medium text-oficina-texto2">{informe ? t('oficinaV2.visorDocumento.informe') : abierto.nombre}</span>
          {!informe ? (
            <a href={urlArchivo(abierto.agentId, abierto.id)} download={abierto.nombre} className="inline-flex h-9 items-center gap-2 rounded-full px-3 text-o-sub text-oficina-texto2 hover:bg-oficina-hover hover:text-oficina-texto">
              <Download className="h-4 w-4" aria-hidden />
              {t('oficinaV2.visorDocumento.descargar')}
            </a>
          ) : null}
          <button ref={boton} type="button" onClick={cerrar} aria-label={t('oficinaV2.visor.cerrar')} className="inline-flex h-9 w-9 items-center justify-center rounded-full text-oficina-texto2 outline-none hover:bg-oficina-hover hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-acento">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {contenido.tipo === 'cargando' ? (
            <p className="flex h-full items-center justify-center gap-2 text-o-texto text-oficina-texto3">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {t('oficinaV2.visorDocumento.abriendo')}
            </p>
          ) : contenido.tipo === 'error' ? (
            <p className="flex h-full items-center justify-center text-o-texto text-oficina-texto3">{t('oficinaV2.visorDocumento.error')}</p>
          ) : contenido.tipo === 'pdf' ? (
            <iframe src={contenido.url} title={abierto.nombre} className="h-full w-full border-0 bg-white" />
          ) : contenido.tipo === 'informe' ? (
            <div className="px-6 py-10 sm:px-12">
              <Informe fuente={contenido.texto} imagen={(src) => imagenDeInforme(abierto.agentId, src)} />
            </div>
          ) : (
            <pre className="whitespace-pre-wrap break-words px-6 py-6 text-o-sub leading-[1.6] text-oficina-texto2">{contenido.texto}</pre>
          )}
        </div>
      </div>
    </div>
  );
}
